import React, { useState, useEffect, useContext } from 'react';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';

import clsx from 'clsx';

import PasswordStrengthChecker from 'app/components/Form/PasswordStrengthChecker';
import useBreakpoint from 'hooks/useBreakpoint/useBreakpoint';
import useEmailValidation from 'hooks/useEmailValidation/useEmailValidation';
import { invalidNPIForCreateProvider } from 'hooks/useMainDataManager/useMainDataManager';
import getProviderDisplayNames from 'utils/providerUtils';

import { AppContext } from '../../containers/Main';
import Footer from '../Footer/Footer';
import * as mainStyles from '../styles';
import { useStyles } from './styles';

const ProviderCredentials = () => {
  const classes = useStyles();
  const mainClasses = mainStyles.useStyles();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'xs';
  const { confirmedProvider, setStep, setStateEmail, setStatePassword }: any = useContext(AppContext);
  const lines = getProviderDisplayNames(confirmedProvider);
  const [password, setPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(false);
  const [emailDirty, setEmailDirty] = useState(false);
  const [passwordDirty, setPasswordDirty] = useState(false);
  const { setEmail, email, emailValid } = useEmailValidation();

  useEffect(() => {
    setStateEmail(emailValid ? email : '');
  }, [email]);

  useEffect(() => {
    setStatePassword(password);
  }, [password]);

  const handleEmailChange = e => {
    setEmailDirty(true);
    setEmail(e.target.value);
  };
  const handleNext = () => {
    setStep(3);
  };
  const goBack = () => {
    setStep(1);
  };

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    // @ts-ignore
    setAnchorEl(null);
    setOpen(false);
    setPasswordValid(passwordValid);
  };

  const handlePassword = e => {
    setPasswordDirty(true);
    const value = e.currentTarget.value;
    if (
      !!value.length &&
      8 <= value.length &&
      !!value.match(new RegExp('[A-Z]')) &&
      !!value.match(new RegExp('[a-z]')) &&
      // prettier-ignore
      !!value.match(new RegExp('[0-9!#$%&\'()*+,\-.:;<=>?@[\\]^_`{|}~]'))
    ) {
      setPasswordValid(true);
    } else {
      setPasswordValid(false);
    }
    setPassword(value);
  };

  return (
    <>
      <div className={classes.goBack}>
        <a href="javascript:void(0)" onClick={goBack}>
          ‹ Go back
        </a>
      </div>
      {confirmedProvider.npi_id !== invalidNPIForCreateProvider ? (
        <>
          <div className={classes.providerCredentialsContainer}>
            <div className={classes.providerPillContainer}>
              {confirmedProvider.real_photo_path ? (
                <img
                  className={clsx({
                    [mainClasses.providerPreviewImgPill]: !isMobile,
                    [mainClasses.providerPreviewImgPillMobile]: isMobile,
                  })}
                  src={confirmedProvider.real_photo_path}
                  alt="provider"
                />
              ) : (
                <div
                  className={clsx({
                    [mainClasses.providerPreviewImgPill]: !isMobile,
                    [mainClasses.providerPreviewImgPillMobile]: isMobile,
                  })}
                >
                  <svg>
                    <use
                      xlinkHref={
                        confirmedProvider.gender && confirmedProvider.gender === 'F'
                          ? '#icon-provider-female'
                          : '#icon-provider-male'
                      }
                    />
                  </svg>
                </div>
              )}
              <div className={classes.providerPillText}>
                <div className={mainClasses.providerPillName} title={lines[0]}>
                  {lines[0]}
                </div>
                <div className={mainClasses.providerPillAddress} title={lines[1]}>
                  {lines[1]}
                </div>
                <div className={mainClasses.providerPillAddress} title={lines[1]}>
                  {lines[2]}
                </div>
              </div>
            </div>
          </div>
          <p className={classes.credentialsText}>
            Alright! Now that we’ve found your profile, we’re going to create your secure account. Please enter your
            email and set a strong password.
          </p>
        </>
      ) : (
        <>
          <p className={`${classes.credentialsText} ${classes.topMargin}`}>
            Once you create a secure account, you're going to be able to create a provider profile in our system and
            verify your identity. That'll let our security team confirm your data and publish your profile.
          </p>
          <Divider variant="middle" />
          <p className={classes.credentialsText}>To create your account, enter your email and a strong password.</p>
        </>
      )}

      <Grid container spacing={3} style={{height: 130}}>
        <Grid item xs={12} sm={6}>
          <div className={mainClasses.labelForInput}>Your Email</div>
          <TextField
            id="email"
            placeholder="Email"
            value={email}
            onChange={handleEmailChange}
            fullWidth
            variant="outlined"
            helperText={!emailValid && emailDirty ? 'Invalid Email.' : ''}
            error={!emailValid && emailDirty}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={mainClasses.labelForInput}>Set a Password</div>
          <TextField
            id="password"
            placeholder="Password"
            value={password}
            onChange={handlePassword}
            fullWidth
            type="password"
            variant="outlined"
            onFocus={handleOpen}
            onBlur={handleClose}
            helperText={!passwordValid && passwordDirty ? 'Invalid Password.' : ''}
            error={!passwordValid && passwordDirty}
          />
          <PasswordStrengthChecker anchorEl={anchorEl} open={open} password={password} />
        </Grid>
        <Grid item xs={12} justify="flex-end">
          <Footer buttonLabel="Next" buttonCallback={handleNext} buttonDisabled={!emailValid || !passwordValid} />
        </Grid>
      </Grid>
    </>
  );
};
export default ProviderCredentials;
